import { GameConfig } from "../config/GameConfig";
import { ClueImages } from "../data/ClueImages";

export type ReferenceScreenName = "home" | "levels" | "picture" | "fill" | "success" | "failure" | "wallet" | "rank";

type Crop = { x: number; y: number; width: number; height: number };
type Layout = Crop & { scale: number; offsetX: number; offsetY: number };
export type StageRect = { x: number; y: number; width: number; height: number; scale: number };

const CROPS: Record<ReferenceScreenName, Crop> = {
    home: { x: 56, y: 64, width: 456, height: 951 },
    levels: { x: 555, y: 64, width: 461, height: 951 },
    picture: { x: 1056, y: 64, width: 462, height: 951 },
    fill: { x: 1559, y: 64, width: 458, height: 951 },
    success: { x: 56, y: 1057, width: 456, height: 951 },
    failure: { x: 555, y: 1057, width: 461, height: 951 },
    wallet: { x: 1056, y: 1057, width: 462, height: 951 },
    rank: { x: 1559, y: 1057, width: 458, height: 951 },
};

export class ReferenceUI {
    static readonly ASSET = "resources/ui/reference_screens.png";
    private static source: Laya.Texture;
    private static textures = new Map<ReferenceScreenName, Laya.Texture>();

    static async preload(): Promise<void> {
        this.source = await Laya.loader.load(this.ASSET) as Laya.Texture;
        if (!this.source) throw new Error(`无法加载参考 UI：${this.ASSET}`);
        await Laya.loader.load(ClueImages.ASSETS);
    }

    static createScreen(name: ReferenceScreenName): Laya.Sprite {
        const crop = CROPS[name];
        const layout = this.layout(name);
        let texture = this.textures.get(name);
        if (!texture) {
            texture = Laya.Texture.createFromTexture(this.source, crop.x, crop.y, crop.width, crop.height);
            this.textures.set(name, texture);
        }

        const sprite = new Laya.Sprite();
        sprite.name = `reference_${name}`;
        sprite.width = GameConfig.DESIGN_WIDTH;
        sprite.height = GameConfig.DESIGN_HEIGHT;
        sprite.mouseEnabled = false;
        sprite.graphics.drawTexture(
            texture,
            layout.offsetX,
            layout.offsetY,
            crop.width * layout.scale,
            crop.height * layout.scale,
        );
        return sprite;
    }

    /** Creates a stage-scaled region from one reference screen for layered animation. */
    static createRegion(
        name: ReferenceScreenName,
        sourceX: number,
        sourceY: number,
        sourceWidth: number,
        sourceHeight: number,
    ): Laya.Sprite {
        const crop = CROPS[name];
        const rect = this.sourceRect(name, sourceX, sourceY, sourceWidth, sourceHeight);
        const texture = Laya.Texture.createFromTexture(
            this.source,
            crop.x + sourceX,
            crop.y + sourceY,
            sourceWidth,
            sourceHeight,
        );
        const sprite = new Laya.Sprite();
        sprite.width = rect.width;
        sprite.height = rect.height;
        sprite.mouseEnabled = false;
        sprite.graphics.drawTexture(texture, 0, 0, rect.width, rect.height);
        return sprite;
    }

    static addHit(
        owner: Laya.Sprite,
        screen: ReferenceScreenName,
        sourceX: number,
        sourceY: number,
        sourceWidth: number,
        sourceHeight: number,
        onClick: () => void,
        name = "hit",
    ): Laya.Box {
        const layout = this.layout(screen);
        const hit = new Laya.Box();
        hit.name = name;
        hit.width = sourceWidth * layout.scale;
        hit.height = sourceHeight * layout.scale;
        hit.pos(layout.offsetX + sourceX * layout.scale, layout.offsetY + sourceY * layout.scale);
        hit.mouseEnabled = true;
        hit.graphics.drawRect(0, 0, hit.width, hit.height, "#FFFFFF01");
        hit.on(Laya.Event.CLICK, hit, onClick);
        owner.addChild(hit);
        return hit;
    }

    static sourceRect(
        screen: ReferenceScreenName,
        sourceX: number,
        sourceY: number,
        sourceWidth: number,
        sourceHeight: number,
    ): StageRect {
        const layout = this.layout(screen);
        return {
            x: layout.offsetX + sourceX * layout.scale,
            y: layout.offsetY + sourceY * layout.scale,
            width: sourceWidth * layout.scale,
            height: sourceHeight * layout.scale,
            scale: layout.scale,
        };
    }

    private static layout(name: ReferenceScreenName): Layout {
        const crop = CROPS[name];
        const scale = Math.max(GameConfig.DESIGN_WIDTH / crop.width, GameConfig.DESIGN_HEIGHT / crop.height);
        return {
            ...crop,
            scale,
            offsetX: (GameConfig.DESIGN_WIDTH - crop.width * scale) / 2,
            offsetY: (GameConfig.DESIGN_HEIGHT - crop.height * scale) / 2,
        };
    }
}
